import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, session, type CreditsView } from "./api";
import { identify, resetIdentity, capture } from "./analytics";

// Accounts are the spine of the web experience: the credit ledger, the library,
// and print orders all hang off the account, and the Worker unions it with any
// linked device. Signing in on web is what makes "buy here, capture on your
// phone, print from the app" one continuous thing.

interface AuthState {
  signedIn: boolean;
  email: string | null;
  credits: CreditsView | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signIn: (token: string, email: string, userId: string) => void;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [signedIn, setSignedIn] = useState(session.signedIn);
  const [email, setEmail] = useState<string | null>(session.email);
  const [credits, setCredits] = useState<CreditsView | null>(null);
  const [loading, setLoading] = useState(session.signedIn);

  async function refresh() {
    if (!session.signedIn) {
      setCredits(null);
      setLoading(false);
      return;
    }
    try {
      const me = await api.me();
      if (!me.signedIn) {
        // The server disagrees — trust it and drop the dead session.
        session.clear();
        setSignedIn(false);
        setEmail(null);
        setCredits(null);
      } else {
        setEmail(me.email ?? null);
        setCredits(me.credits ?? null);
      }
    } catch {
      // Offline or a blip: keep what we have rather than logging the user out.
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Ctx.Provider
      value={{
        signedIn,
        email,
        credits,
        loading,
        refresh,
        signIn: (token, mail, userId) => {
          session.save(token, mail);
          setSignedIn(true);
          setEmail(mail);
          identify(userId, mail);
          void refresh();
        },
        signOut: async () => {
          try {
            await api.signOut();
          } catch {
            // A failed revoke server-side must not strand the user signed in here.
          }
          session.clear();
          resetIdentity();
          setSignedIn(false);
          setEmail(null);
          setCredits(null);
          capture("signedOut");
        },
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
